import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Card,
  Spinner,
  Form,
  Container,
} from "react-bootstrap";
import { Context } from "../App";
import * as Constants from "../api/constants";
import axios from "axios";
import Toaster from "./Toaster";
import { loadBookingFlow } from "../utils/bookingFlowStorage";
import { useCms } from "../context/CmsContext";
import { formatDateDisplayEnglish, toDateInputValue } from "../utils/eventDate";
import {
  savePendingCheckout,
  clearPendingCheckout,
} from "../utils/razorpayReturn";
import { normalizeYmd } from "../utils/slotTimeOffers";

const ADVANCE_INCL_FEE = 700;

function peekBookingTrafficSource() {
  try {
    const v = sessionStorage.getItem("pb_booking_traffic_source");
    if (!v) return null;
    const t = String(v).trim();
    if (!t) return null;
    return t.length > 120 ? t.slice(0, 120) : t;
  } catch {
    return null;
  }
}

const RAZORPAY_CHECKOUT_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

/** Ensures Razorpay Standard Checkout JS is loaded (CDN blocked / slow builds). */
function loadRazorpayCheckoutScript() {
  const waitForConstructor = () =>
    new Promise((resolve, reject) => {
      const deadlineMs = Date.now() + 15000;
      (function poll() {
        if (typeof window !== "undefined" && typeof window.Razorpay === "function") {
          resolve();
          return;
        }
        if (Date.now() > deadlineMs) {
          reject(new Error("Razorpay checkout script timeout"));
          return;
        }
        window.setTimeout(poll, 40);
      })();
    });

  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && typeof window.Razorpay === "function") {
      resolve();
      return;
    }
    if (typeof document === "undefined") {
      reject(new Error("document unavailable"));
      return;
    }
    const existing = document.querySelector(
      `script[src="${RAZORPAY_CHECKOUT_SCRIPT}"]`
    );

    const startWait = () => {
      waitForConstructor().then(resolve).catch(reject);
    };

    if (existing) {
      existing.addEventListener(
        "error",
        () => reject(new Error("Razorpay script failed")),
        { once: true }
      );
      existing.addEventListener("load", startWait, { once: true });
      // index.html loads the script synchronously in <head>; "load" may have fired already.
      startWait();
      return;
    }

    const script = document.createElement("script");
    script.src = RAZORPAY_CHECKOUT_SCRIPT;
    script.async = true;
    script.onload = () => startWait();
    script.onerror = () => reject(new Error("Razorpay script failed"));
    document.body.appendChild(script);
  });
}

const overviewRowClass =
  "flex min-w-0 items-start gap-1.5 px-2 py-1 sm:gap-2 sm:px-3 sm:py-1";

const overviewIconClass =
  "mt-0.5 shrink-0 text-sm leading-none text-[#ffd166] sm:text-base";

const cardShell =
  "overflow-hidden rounded-2xl border border-white/15 bg-[#0a0614] shadow-[0_12px_40px_-16px_rgba(0,0,0,0.45)] ring-1 ring-white/[0.06]";

function ReviewAddons(props) {
  const { selectedTheaterByDate, selectedTime } = useContext(Context);

  const theaterForBooking =
    selectedTheaterByDate?.id != null
      ? selectedTheaterByDate
      : props.restoredTheater && props.restoredTheater.id != null
        ? props.restoredTheater
        : {};

  const resolveSlot = (st, restored) => {
    if (st && typeof st === "object" && !Array.isArray(st) && st.id != null)
      return st;
    if (Array.isArray(st) && st.length && st[0]?.id != null) return st[0];
    return restored && typeof restored === "object" ? restored : {};
  };
  const slotForBooking = resolveSlot(selectedTime, props.restoredSlot);

  const [showSpinner, setShowSpinner] = useState(false);
  const [showInstructions, setshowInstructions] = useState(false);
  const [change, setChange] = useState(false);

  // Promo code state — applied promo wins until the customer removes/clears it.
  const [promoInput, setPromoInput] = useState("");
  const [promoApplying, setPromoApplying] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discountAmount }

  const [toasterDetails, setToasterDetails] = useState(null);
  const { setContext } = useContext(Context);
  const navigate = useNavigate();
  const pendingBookingRef = useRef(null);
  const paymentCompletedRef = useRef(false);
  const embed = Boolean(props.embed);
  const adminBooking = Boolean(props.adminBooking);

  const { cms } = useCms();
  const bf = cms.bookingForm;

  /** Time-slot promo from booking draft — disables stacking promo codes (server enforced too). */
  const slotOfferFromDraft = loadBookingFlow()?.slotOffer;
  const slotOfferBlocksPromo = Boolean(slotOfferFromDraft?.active);

  useEffect(() => {
    if (!slotOfferBlocksPromo) return;
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  }, [slotOfferBlocksPromo]);

  /** Contact captured on BookingFormPage (session draft); props override when passed. */
  const contact = useMemo(() => {
    const d = loadBookingFlow();
    return {
      customerName: String(
        props.bookingName ?? d?.bookingName ?? ""
      ).trim(),
      phoneNumber: String(props.whatsapp ?? d?.whatsapp ?? "").trim(),
      email: String(props.email ?? d?.email ?? "").trim(),
    };
  }, [props.bookingName, props.whatsapp, props.email]);

  const submitFn = (event) => {
    event?.preventDefault?.();
    const { customerName, phoneNumber, email } = contact;
    if (
      !customerName ||
      !/^\d{10}$/.test(phoneNumber) ||
      !/^\S+@\S+\.\S+$/.test(email)
    ) {
      setToasterDetails({
        variant: "Danger",
        message:
          "Missing valid contact details. Go back to the booking form and complete name, WhatsApp, and email.",
      });
      return;
    }
    setChange(false);
    setshowInstructions(true);
  };

  const overviewDate = useMemo(
    () => formatDateDisplayEnglish(props.selectedValues?.eventDate ?? ""),
    [props.selectedValues?.eventDate]
  );

  const slotLabel = String(slotForBooking?.slotTime || "").trim() || "—";

  const summaryFigures = useMemo(() => {
    const d = loadBookingFlow();
    const theatreBase = Number(d?.theatreBaseCost ?? 0);
    const decorationEst = Number(d?.decorationEstimate ?? 0);
    const subtotal = Number(props.totalCost ?? 0);
    const addonsTotal = Math.max(0, subtotal - theatreBase);
    const packageOnly = Math.max(0, theatreBase - decorationEst);
    const promoDiscount = Math.min(
      Number(appliedPromo?.discountAmount || 0),
      subtotal
    );
    const reviewTotal = Math.max(0, subtotal - promoDiscount);
    const balanceAtVenue = Math.max(0, reviewTotal - ADVANCE_INCL_FEE);

    const so = d?.slotOffer;
    let slotOfferHeadline = "";
    let slotOfferSavingsDisplay = "";
    let slotOfferPackageDiscount = 0;
    if (so?.active) {
      slotOfferHeadline =
        so.headline ||
        "A limited time-slot offer is included in your package price.";
      const reg = Number(so.regularSlotBasePrice ?? 0);
      const pay = Number(so.offerBasePrice ?? 0);
      const k = String(so.offerPriceKind || "RS").toUpperCase();
      if (reg >= 0 && pay >= 0) {
        slotOfferPackageDiscount = Math.max(0, Math.round(reg - pay));
        if (k === "PCT") {
          const pct = Math.round(Number(so.offerRawValue ?? 0));
          slotOfferSavingsDisplay = `Regular slot base ₹${reg} → ${pct}% off → you pay ₹${pay}`;
        } else if (reg > pay) {
          slotOfferSavingsDisplay = `Slot base ₹${reg} → offer ₹${pay}`;
        } else if (reg === pay && reg > 0) {
          slotOfferSavingsDisplay = `Package base for this slot: ₹${pay}`;
        }
      }
    }

    return {
      theatreBase,
      subtotal,
      promoDiscount,
      reviewTotal,
      addonsTotal,
      packageOnly,
      balanceAtVenue,
      slotOfferHeadline,
      slotOfferSavingsDisplay,
      slotOfferPackageDiscount,
    };
  }, [props.totalCost, appliedPromo]);

  const onApplyPromo = useCallback(async () => {
    setPromoError("");
    const code = promoInput.trim();
    if (!code) {
      setPromoError("Enter a promo code");
      return;
    }
    if (!/^\d{10}$/.test(contact.phoneNumber)) {
      setPromoError("Add your 10-digit phone number above first");
      return;
    }
    setPromoApplying(true);
    try {
      const { data } = await axios.post(Constants.PROMO_VALIDATE, {
        code,
        phoneNumber: contact.phoneNumber,
        bookingAmount: Number(props.totalCost ?? 0),
        theatreId: theaterForBooking?.id ?? null,
        slotId:
          slotForBooking?.id != null ? Number(slotForBooking.id) : null,
        bookingDate: normalizeYmd(props.selectedValues?.eventDate) || null,
      });
      if (data?.valid) {
        setAppliedPromo({
          code: data.code || code.toUpperCase(),
          discountAmount: Number(data.discountAmount || 0),
        });
        setPromoError("");
      } else {
        setAppliedPromo(null);
        setPromoError(data?.message || "Promo code is not valid");
      }
    } catch (err) {
      setAppliedPromo(null);
      const msg =
        err?.response?.data?.message || "Could not validate promo. Try again.";
      setPromoError(msg);
    } finally {
      setPromoApplying(false);
    }
  }, [
    promoInput,
    contact.phoneNumber,
    props.totalCost,
    theaterForBooking?.id,
    slotForBooking?.id,
    props.selectedValues?.eventDate,
  ]);

  const onRemovePromo = useCallback(() => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  }, []);

  const contactSummaryModal = (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-start">
      <p className="mb-2 border-b border-slate-200 pb-2">
        <span className="text-muted small d-block">Name</span>
        <span className="fw-semibold text-dark">
          {contact.customerName || "—"}
        </span>
      </p>
      <p className="mb-2 border-b border-slate-200 pb-2">
        <span className="text-muted small d-block">WhatsApp</span>
        <span className="fw-semibold text-dark">
          {contact.phoneNumber || "—"}
        </span>
      </p>
      <p className="mb-2">
        <span className="text-muted small d-block">Email</span>
        <span className="fw-semibold text-dark text-break">
          {contact.email || "—"}
        </span>
      </p>
    </div>
  );
  const renderSelectedProps = (addons) => {
    switch (addons.categoryType) {
      case "CAKES":
        return (
          <li>
            <img src={require('../images/birthday-cake.svg').default} width={"30px"} alt="" />{`${addons.itemName} cake`} - <span className="text-danger"><i className="bi bi-currency-rupee"></i>{addons.price}</span>
              
          </li>
        );
      case "DECORATION":
        return (
          <li>
           <img src={require('../images/ballon.svg').default} width={"30px"} alt="" />
{addons.itemName} - <span className="text-danger"><i className="bi bi-currency-rupee"></i>{addons.price}</span>
             
          </li>
        );
      case "PARTY_PROPS":
        return (
          <li>
            <img src={require('../images/props.svg').default} width={"30px"} alt="" /> {addons.itemName} {addons.categoryType} - <span className="text-danger"><i className="bi bi-currency-rupee"></i>{addons.price}</span>
              
          </li>
        );
      case "PHOTOS":
        return (
          <li>
            <img src={require('../images/camera.svg').default} width={"30px"} alt="" />Photography {addons.itemName} - <span className="text-danger"><i className="bi bi-currency-rupee"></i>{addons.price}</span>
              
          </li>
        );
      default:
        return (
          <li>
            <img src={require('../images/flowers-img.svg').default} width={"30px"} alt="" />
              
                {addons.itemName} {addons.categoryType} - <span className="text-danger"><i className="bi bi-currency-rupee"></i>{addons.price}</span>
            
          </li>
        );
    }
  };

  const proceedToRazorPay = async (csReponse) => {
    pendingBookingRef.current = csReponse.data;
    paymentCompletedRef.current = false;
    const payDetails = csReponse.data.payDetails;
    savePendingCheckout({
      eventId: csReponse.data?.eventId,
      bookingReferenceId: csReponse.data?.bookingReference,
    });

    try {
      await loadRazorpayCheckoutScript();
    } catch {
      setShowSpinner(false);
      setChange(false);
      setToasterDetails({
        variant: "Danger",
        message:
          "Razorpay could not load (blocked script or network). Allow checkout.razorpay.com and reload.",
      });
      return;
    }

    if (typeof window.Razorpay !== "function") {
      setShowSpinner(false);
      setChange(false);
      setToasterDetails({
        variant: "Danger",
        message:
          "Razorpay could not load (blocked script or network). Allow Razorpay/checkout and reload.",
      });
      return;
    }

    // Razorpay handler confirms payment via /updatePd before we navigate (no callback_url redirect).
    const amount =
      typeof payDetails.amount === "number"
        ? payDetails.amount
        : Number(payDetails.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setShowSpinner(false);
      setChange(false);
      setToasterDetails({
        variant: "Danger",
        message:
          "Invalid payment amount from server. Try again or contact Party Boss.",
      });
      return;
    }

    var options = {
      key: payDetails.keyId,
      amount,
      currency: payDetails.currency || "INR",
      name: payDetails.businessName,
      description: payDetails.description,
      image: payDetails.logoUrl || undefined,
      order_id: payDetails.orderId,
      prefill: {
        name: payDetails.custName,
        email: payDetails.custEmail,
        contact: payDetails.custPhone,
      },
      handler(response) {
        paymentCompletedRef.current = true;
        setShowSpinner(true);
        updatePaymentDetails(response, csReponse.data);
      },
      modal: {
        escape: false,
        ondismiss() {
          if (paymentCompletedRef.current) {
            return;
          }
          const cs = pendingBookingRef.current;
          if (cs?.eventId) {
            axios.post(Constants.MARK_PAYMENT_ABANDONED, { id: cs.eventId }).catch(() => {});
          }
          showEventDetails("Payment window closed");
        },
      },
      notes: {
        address: "Razorpay Corporate Office",
      },
      theme: {
        color: "#3399cc",
      },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.on("payment.submit", (ev) => {
      console.log("msg=Payment method : ", ev.method);
    });
    rzp1.on("payment.failed", function (ev) {
      console.log("Handling fail", ev);
      const cs = pendingBookingRef.current;
      const failPayload = {
        razorpay_order_id: "",
        error: ev?.error || {
          reason: "PAYMENT_FAILED",
          description: "Payment failed",
          metadata: {},
        },
      };
      if (cs) {
        updatePaymentDetails(failPayload, cs);
      }
    });

    setShowSpinner(false);
    setshowInstructions(false);
    rzp1.open();
  };

  const navigateAfterBooking = (path) => {
    if (adminBooking) {
      navigate("/admin/dashboard");
      return;
    }
    navigate(path);
  };

  const showEventDetails = (status) => {
    setToasterDetails({
      variant: "Success",
      message: status,
    });
    setTimeout(() => {
      navigateAfterBooking("/event-details");
    }, adminBooking ? 800 : 2000);
  };
  const handleAgreementChange = (e) => {
    setChange(Boolean(e?.target?.checked));
  };

  /** Close instructions step and reset agreement so returning requires checking the box again. */
  const closeInstructionsModal = () => {
    setChange(false);
    setshowInstructions(false);
  };

  const updatePaymentDetails = (rpResponse, csDetails) => {
    const isSuccess = Boolean(
      rpResponse?.razorpay_order_id && rpResponse?.razorpay_payment_id
    );
    let paystatus;
    let failReason = "";
    let orderId = "";
    let paymentId = "";
    let signature = "";
    if (isSuccess) {
      orderId = rpResponse.razorpay_order_id;
      paymentId = rpResponse.razorpay_payment_id;
      signature = rpResponse.razorpay_signature || "";
      paystatus = "SUCCESS";
    } else {
      failReason =
        rpResponse?.error?.reason ||
        rpResponse?.error?.description ||
        "Payment failed";
      orderId = rpResponse?.error?.metadata?.order_id || "";
      paymentId = rpResponse?.error?.metadata?.payment_id || "";
      paystatus = "FAILED";
    }
    const reqObj = {
      id: csDetails?.eventId ?? csDetails?.id,
      paymentResponse: {
        status: paystatus,
        orderId,
        paymentId,
        signature,
        failedReason: failReason,
        amountReceived: csDetails?.payDetails?.amount,
      },
    };

    return axios
        .post(Constants.UPDATE_PD, { ...reqObj })
        .then((response) => {
          let { bookingReference, status } = response?.data;
          setShowSpinner(false);
          if (paystatus === "FAILED") {
            setContext((prev) => ({
              ...(prev && typeof prev === "object" ? prev : {}),
              bookingReferenceId: bookingReference ?? prev?.bookingReferenceId,
            }));
            setToasterDetails({
              variant: "Danger",
              message:
                status ||
                failReason ||
                "Payment failed — details recorded.",
            });
            return;
          }
          clearPendingCheckout();
          setContext((prev) => ({
            ...(prev && typeof prev === "object" ? prev : {}),
            bookingReferenceId: bookingReference,
          }));
          setToasterDetails({
            variant: "Success",
            message: `${status} and Reference Number is ${bookingReference}`,
          });
          setTimeout(() => {
            navigateAfterBooking("/event-details");
          }, 1000);
        })
        .catch((error) => {
          console.log(error);
          setShowSpinner(false);
          setToasterDetails({
            variant: "Danger",
            message:
              error?.response?.data?.message ||
              "Unable to Book the Event please try again",
          });
        });
  };

  const buildBookPayload = () => {
    const { eventDate, eventId, personsCount, slotId } = props.selectedValues;
    return {
      eventDate: toDateInputValue(eventDate),
      customerName: contact.customerName,
      phoneNumber: contact.phoneNumber,
      email: contact.email,
      slotId: Number(slotId),
      theatreId: theaterForBooking?.id,
      eventTypeId: Number(eventId),
      selectedSeats: Number(personsCount),
      extraSeats:
        Number(slotForBooking?.defaultCapacity) - Number(personsCount) > 0
          ? Number(slotForBooking?.defaultCapacity) - Number(personsCount)
          : 0,
      defaultSeats: Number(slotForBooking?.defaultCapacity),
      addons: Object.values(props.selectedAddons),
      theatreName: theaterForBooking?.name,
      nameOnCake: props.nameOnCake,
      paymentType: "ONLINE",
      promoCode: appliedPromo?.code || null,
      promoDiscountAmount: appliedPromo?.discountAmount || 0,
      testMode: Constants.IS_LOCAL_TEST_MODE,
      bookingTrafficSource: adminBooking
        ? "admin_slot_booking"
        : peekBookingTrafficSource(),
      adminBooking: adminBooking || undefined,
    };
  };

  const saveAndPay = () => {
    const reqObj = buildBookPayload();
      axios
        .post(Constants.BOOK_NOW_AND_PAY, { ...reqObj })
        .then(async (response) => {
          console.log("msg=Response : ", response);
          const orderId = response?.data?.payDetails?.orderId;

          // Localhost test mode: bypass Razorpay entirely. Build a TEST-* payment response so
          // /updatePd marks the booking as paid (and the backend recognises the prefix to skip
          // admin email notifications).
          if (Constants.IS_LOCAL_TEST_MODE) {
            // eslint-disable-next-line no-console
            console.info(
              "[TestMode] localhost detected — skipping Razorpay and confirming booking via TEST- payment id."
            );
            const fakeRpResponse = {
              razorpay_order_id:
                orderId || `TEST-ORDER-${Date.now()}`,
              razorpay_payment_id: `TEST-PAYMENT-${Date.now()}`,
              razorpay_signature: "TEST-SIGNATURE",
            };
            try {
              setShowSpinner(true);
              updatePaymentDetails(fakeRpResponse, response.data);
            } catch (e) {
              setShowSpinner(false);
              showEventDetails("Payment Not successful");
            }
            return;
          }

          if (orderId != null && orderId !== "") {
            try {
              await proceedToRazorPay(response);
            } catch (e) {
              setShowSpinner(false);
              setChange(false);
              console.error(e);
              setToasterDetails({
                variant: "Danger",
                message:
                  "Could not open Razorpay. Check your internet connection or use HTTPS if you pay with live mode.",
              });
            }
          } else {
            setShowSpinner(false);
            showEventDetails("Payment Not successful");
          }
        })
        .catch((error) => {
          console.log(error);
          setShowSpinner(false);
          setChange(false);
          setToasterDetails({
            variant: "Danger",
            message:
              error?.response?.data?.message ||
              "Unable to Book the Event please try again",
          });
        });

  }

  const savePayNow = async () => {
    setShowSpinner(true);
    saveAndPay();
  };

  const savePayLater = () => {
    setShowSpinner(true);
    const reqObj = buildBookPayload();
    const payLaterPayload = adminBooking
      ? { ...reqObj, paymentType: "Pending" }
      : reqObj;
    axios
      .post(Constants.BOOK_NOW_AND_PAY_LATER, { ...payLaterPayload })
      .then((response) => {
        const bookingReference =
          response?.data?.bookingReference || response?.data?.bookingRefId;
        setShowSpinner(false);
        setshowInstructions(false);
        setChange(false);
        setContext((prev) => ({
          ...(prev && typeof prev === "object" ? prev : {}),
          bookingReferenceId: bookingReference,
        }));
        setToasterDetails({
          variant: "Success",
          message: adminBooking
            ? `Slot held — pending payment. Ref ${bookingReference}`
            : `Booking saved. Ref ${bookingReference}`,
        });
        setTimeout(() => {
          navigateAfterBooking("/event-details");
        }, adminBooking ? 1200 : 2000);
      })
      .catch((error) => {
        setShowSpinner(false);
        setChange(false);
        setToasterDetails({
          variant: "Danger",
          message:
            error?.response?.data?.message ||
            "Unable to save booking — please try again",
        });
      });
  };

  const spinnerOverlay = showSpinner ? (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 11000,
        background: "rgba(0, 0, 0, 0.45)",
      }}
    >
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  ) : null;

  if (embed) {
    return (
      <>
        {toasterDetails && <Toaster details={toasterDetails} />}
        {spinnerOverlay}
        <section className="party-boss-page w-full bg-[#05020a] pb-12 pt-6 text-white selection:bg-pink-500/40 md:pb-16 md:pt-8">
          <div className="party-boss-bg relative z-10 px-3 sm:px-4 md:px-6">
            <p className="text-center text-[10px] font-black uppercase tracking-widest text-[#ffd166] sm:text-xs">
              Review &amp; pay
            </p>
            <h1 className="mb-1 text-center text-xl font-black text-white md:text-3xl">
              Confirm your booking
            </h1>
            <p className="mx-auto mb-4 max-w-xl text-center text-xs text-[#c9c1d9] sm:text-sm">
              Check overview and summary — same layout as your booking step —
              then proceed to pay the advance.
            </p>
            {!showInstructions ? (
              <Container className="mx-auto max-w-4xl p-0">
                <div className={cardShell}>
                  <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/30 via-fuchsia-600/20 to-violet-700/30 px-4 py-3">
                    <p className="text-center text-[11px] font-black uppercase tracking-[0.18em] text-[#ffd166]">
                      Your booking review
                    </p>
                  </div>

                  <div className="grid bg-[#12081f]/40 md:grid-cols-2 md:divide-x md:divide-white/10">
                    <div>
                      <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/20 to-violet-700/20 px-3 py-2 sm:px-4">
                        <h2 className="text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                          {bf.overviewLabel}
                        </h2>
                      </div>
                      <div className="divide-y divide-white/10 text-sm">
                        <div className={overviewRowClass}>
                          <i
                            className={`bi bi-building ${overviewIconClass}`}
                            aria-hidden
                          />
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                            {bf.overviewTheatreLabel}
                          </span>
                          <span className="min-w-0 flex-1 break-words text-xs font-semibold leading-snug text-white sm:text-sm">
                            {theaterForBooking?.name || "—"}
                          </span>
                        </div>
                        <div className={overviewRowClass}>
                          <i
                            className={`bi bi-calendar3 ${overviewIconClass}`}
                            aria-hidden
                          />
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                            {bf.overviewDateLabel}
                          </span>
                          <span className="min-w-0 flex-1 text-xs font-semibold leading-snug text-[#ffd166] sm:text-sm">
                            {overviewDate || "—"}
                          </span>
                        </div>
                        <div className={overviewRowClass}>
                          <i
                            className={`bi bi-clock ${overviewIconClass}`}
                            aria-hidden
                          />
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                            {bf.overviewSlotLabel}
                          </span>
                          <span className="min-w-0 flex-1 whitespace-pre-line break-words text-xs font-semibold leading-snug text-white sm:text-sm">
                            {slotLabel}
                          </span>
                        </div>
                        <div className={overviewRowClass}>
                          <i
                            className={`bi bi-people ${overviewIconClass}`}
                            aria-hidden
                          />
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                            Persons:
                          </span>
                          <span className="min-w-0 flex-1 text-xs font-semibold leading-snug text-white sm:text-sm">
                            {props.selectedValues?.personsCount ?? "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-white/10 md:border-t-0">
                      <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/20 to-violet-700/20 px-3 py-2 sm:px-4">
                        <h2 className="text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                          Customer details
                        </h2>
                      </div>
                      <div className="divide-y divide-white/10 text-sm">
                        <div className={overviewRowClass}>
                          <i
                            className={`bi bi-person-circle ${overviewIconClass}`}
                            aria-hidden
                          />
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                            Name:
                          </span>
                          <span className="min-w-0 flex-1 break-words text-xs font-semibold leading-snug text-white sm:text-sm">
                            {contact.customerName || "—"}
                          </span>
                        </div>
                        <div className={overviewRowClass}>
                          <i
                            className={`bi bi-whatsapp ${overviewIconClass}`}
                            aria-hidden
                          />
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                            WhatsApp:
                          </span>
                          <span className="min-w-0 flex-1 text-xs font-semibold leading-snug text-white sm:text-sm">
                            {contact.phoneNumber || "—"}
                          </span>
                        </div>
                        <div className={overviewRowClass}>
                          <i
                            className={`bi bi-envelope-at ${overviewIconClass}`}
                            aria-hidden
                          />
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[#94a3b8]">
                            Email:
                          </span>
                          <span className="min-w-0 flex-1 break-all text-xs font-semibold leading-snug text-white sm:text-sm">
                            {contact.email || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-white/10">
                    <div className="border-b border-white/10 bg-gradient-to-r from-pink-600/20 to-violet-700/20 px-3 py-2 sm:px-4">
                      <h3 className="text-[10px] font-black uppercase tracking-widest text-[#ffd166]">
                        {bf.summaryTitle}
                      </h3>
                    </div>
                    <div className="grid gap-4 px-3 py-3 sm:px-4 md:grid-cols-2">
                      <div className="space-y-1.5 text-sm">
                        {summaryFigures.slotOfferHeadline ? (
                          <div className="rounded-lg border border-red-500/40 bg-red-950/25 px-2.5 py-2 text-xs text-red-200">
                            <p className="m-0 mb-1 font-bold uppercase tracking-wide text-red-300">
                              Time-slot offer
                            </p>
                            {slotOfferFromDraft?.slotOfferDisplayLabel ? (
                              <p className="m-0 text-[11px] font-semibold text-red-100">
                                {String(slotOfferFromDraft.slotOfferDisplayLabel)}
                              </p>
                            ) : null}
                            <p className="m-0 font-medium leading-snug">
                              {summaryFigures.slotOfferHeadline}
                            </p>
                            {summaryFigures.slotOfferPackageDiscount > 0 ? (
                              <p className="m-0 mt-1 text-[12px] font-bold text-amber-200">
                                You save ₹{summaryFigures.slotOfferPackageDiscount} on the slot package
                                base (before add-ons).
                              </p>
                            ) : null}
                            {summaryFigures.slotOfferSavingsDisplay ? (
                              <p className="m-0 mt-1 text-[11px] text-red-100/90">
                                {summaryFigures.slotOfferSavingsDisplay}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                        <div className="flex justify-between gap-2 text-[#e8e3f0]">
                          <span className="text-xs">{bf.summaryPackage}</span>
                          <span className="font-bold text-white">
                            <i className="bi bi-currency-rupee" />
                            {summaryFigures.packageOnly}
                          </span>
                        </div>
                        {Object.keys(props?.selectedAddons || {}).length > 0 ? (
                          <div className="rounded-lg border border-white/10 bg-[#12081f]/60 px-2.5 py-2">
                            <p className="mb-1.5 text-[10px] font-black uppercase tracking-wide text-[#ffd166]/95">
                              Add-ons
                            </p>
                            <ul className="mb-0 space-y-1">
                              {Object.entries(props.selectedAddons || {}).map(
                                ([key, value]) => (
                                  <li
                                    key={key}
                                    className="flex justify-between gap-2 text-[11px] text-[#e8e3f0]"
                                  >
                                    <span className="min-w-0 truncate">
                                      {value?.itemName}
                                    </span>
                                    <span className="shrink-0 font-bold text-white">
                                      <i className="bi bi-currency-rupee" />
                                      {value?.price}
                                    </span>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-xl border border-[#ffd166]/30 bg-gradient-to-br from-[#ffd166]/[0.08] via-pink-500/[0.06] to-violet-600/[0.08] p-3 sm:p-4">
                        <div className="flex items-baseline justify-between gap-2 text-sm text-[#c9c1d9]">
                          <span>Subtotal</span>
                          <span className="font-bold text-white">
                            <i className="bi bi-currency-rupee" />
                            {summaryFigures.subtotal}
                          </span>
                        </div>

                        <div className="mt-2 rounded-lg border border-white/15 bg-[#12081f]/70 p-2.5">
                          {slotOfferBlocksPromo ? (
                            <p className="m-0 text-[11px] font-semibold leading-snug text-rose-300">
                              Promo codes cannot be combined with this booking — a time-slot
                              promotional price already applies to your selected date and slot.
                            </p>
                          ) : appliedPromo ? (
                            <div className="flex items-center justify-between gap-2 text-xs">
                              <span className="font-semibold text-emerald-300">
                                <i className="bi bi-ticket-perforated-fill me-1" />
                                Promo&nbsp;
                                <code className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-emerald-200">
                                  {appliedPromo.code}
                                </code>
                                &nbsp;applied — saved Rs.{summaryFigures.promoDiscount}
                              </span>
                              <button
                                type="button"
                                className="rounded-md border border-white/20 bg-white/5 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-white/10"
                                onClick={onRemovePromo}
                              >
                                Remove
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1.5">
                              <label
                                className="text-[10px] font-bold uppercase tracking-wider text-[#ffd166]"
                                htmlFor="pb-promo-input"
                              >
                                Have a promo code?
                              </label>
                              <div className="flex gap-2">
                                <input
                                  id="pb-promo-input"
                                  type="text"
                                  value={promoInput}
                                  onChange={(e) =>
                                    setPromoInput(e.target.value.toUpperCase())
                                  }
                                  placeholder="e.g. FLASH500"
                                  className="min-w-0 flex-1 rounded-md border border-white/20 bg-[#1a0d2e] px-2 py-1.5 text-sm font-semibold uppercase tracking-wider text-white placeholder:text-white/30 focus:border-[#ffd166] focus:outline-none"
                                  maxLength={40}
                                  disabled={promoApplying}
                                />
                                <button
                                  type="button"
                                  className="rounded-md bg-gradient-to-r from-pink-500 to-violet-600 px-3 py-1.5 text-xs font-black uppercase tracking-wide text-white transition hover:from-pink-600 hover:to-violet-700 disabled:opacity-60"
                                  onClick={onApplyPromo}
                                  disabled={promoApplying}
                                >
                                  {promoApplying ? "…" : "Apply"}
                                </button>
                              </div>
                              {promoError ? (
                                <p className="m-0 text-[11px] text-rose-300">
                                  {promoError}
                                </p>
                              ) : null}
                            </div>
                          )}
                        </div>

                        {summaryFigures.promoDiscount > 0 ? (
                          <div className="mt-2 flex items-baseline justify-between gap-2 text-sm text-emerald-300">
                            <span>Discount</span>
                            <span className="font-bold">
                              − <i className="bi bi-currency-rupee" />
                              {summaryFigures.promoDiscount}
                            </span>
                          </div>
                        ) : null}

                        <hr className="my-2 border-white/10" />
                        <div className="flex items-baseline justify-between gap-2 text-base font-black text-white">
                          <span>{bf.summaryTotal}</span>
                          <span className="text-xl text-[#ffd166] sm:text-2xl">
                            <i className="bi bi-currency-rupee" />
                            {summaryFigures.reviewTotal}
                          </span>
                        </div>
                        <hr className="my-2 border-white/10" />
                        <div className="flex items-baseline justify-between gap-2 text-xs text-[#c9c1d9]">
                          <span className="font-semibold uppercase tracking-wide">
                            {bf.summaryAdvance}
                          </span>
                          <span className="text-base font-black text-[#ffd166] sm:text-lg">
                            <i className="bi bi-currency-rupee" />
                            {ADVANCE_INCL_FEE}
                          </span>
                        </div>
                        <p className="mt-1 text-[10px] leading-snug text-[#94a3b8]">
                          {bf.summaryAdvanceNote}
                        </p>
                        <hr className="my-2 border-white/10" />
                        <div className="flex items-baseline justify-between gap-2 text-xs text-[#e8e3f0]">
                          <span className="font-semibold uppercase tracking-wide">
                            {bf.summaryBalance}
                          </span>
                          <span className="text-base font-black text-emerald-400 sm:text-lg">
                            <i className="bi bi-currency-rupee" />
                            {summaryFigures.balanceAtVenue}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {Constants.IS_LOCAL_TEST_MODE ? (
                    <div className="border-t border-amber-400/30 bg-amber-400/10 px-3 py-2 text-center text-[10px] font-semibold uppercase tracking-wide text-amber-200 sm:px-4">
                      <i className="bi bi-bug-fill me-1" />
                      Local test mode — payment gateway is bypassed and only the customer email will be sent.
                    </div>
                  ) : null}
                  <div className="flex gap-2 border-t border-white/10 px-3 py-3 sm:px-4">
                    <button
                      type="button"
                      className="flex-1 rounded-xl border border-white/20 bg-white/5 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
                      onClick={() => props?.closeReview?.()}
                    >
                      {bf.backLabel}
                    </button>
                    <button
                      type="button"
                      className="flex-[1.35] rounded-xl bg-gradient-to-r from-pink-500 to-violet-600 py-2.5 text-sm font-black text-white shadow-[0_8px_24px_-8px_rgba(236,72,153,0.6)] transition hover:from-pink-600 hover:to-violet-700"
                      onClick={submitFn}
                    >
                      Proceed to pay <i className="bi bi-arrow-right ms-1" />
                    </button>
                  </div>
                  <p className="border-t border-white/10 px-3 py-2.5 text-center text-[10px] leading-snug text-[#94a3b8] sm:px-4">
                    We collect an advance of ₹{ADVANCE_INCL_FEE} (incl.
                    convenience fee where applicable) to confirm your slot.
                    Balance is due at the venue.
                  </p>
                </div>
              </Container>
            ) : (
              <div className="mx-auto max-w-3xl rounded-3xl border border-white/15 bg-[#0a0614] p-6 text-white">
                <h3 className="mb-3 text-center text-xl font-black">
                  Important instructions
                </h3>
                <p className="rounded-4 mt-2 bg-red-600 px-3 py-2 text-sm text-white md:px-5">
                  Refund policy: partial advance (Rs 500/-) is refundable if you
                  cancel at least 72 hours before your booking time.
                </p>
                <ul className="mt-3 ps-3" style={{ listStyleType: "decimal" }}>
                  <li>
                    Smoking and alcohol are strictly prohibited inside the
                    theatres.
                  </li>
                  <li>Bring your own OTT accounts for content.</li>
                  <li>
                    Party poppers, foam and champagne are not allowed inside the
                    theatres.
                  </li>
                  <li>Outside food is strictly prohibited.</li>
                  <li>
                    We charge full for children 5 years and above; half below 5.
                  </li>
                  <li>Right of admission is reserved by management.</li>
                  <li>
                    If professional photography is chosen, timing is per
                    photographer availability; only soft copies are provided.
                  </li>
                </ul>
                <div className="mt-4 flex justify-content-center">
                  <Form.Check
                    type="checkbox"
                    id="confirmInstructionsEmbed"
                    label="I agree to all the above conditions."
                    checked={change}
                    onChange={handleAgreementChange}
                    className="text-white"
                  />
                </div>
                <div className="mt-6 flex flex-wrap justify-between gap-2">
                  <Button
                    variant="outline-light"
                    onClick={closeInstructionsModal}
                  >
                    Back
                  </Button>
                  <div className="flex flex-wrap gap-2">
                    {adminBooking ? (
                      <Button
                        variant="outline-light"
                        className="text-white border-white"
                        onClick={() => savePayLater()}
                        disabled={!change}
                      >
                        Save and Pay Later
                      </Button>
                    ) : null}
                    <Button
                      variant="danger"
                      onClick={() => savePayNow()}
                      disabled={!change}
                      id="rzp-button-embed"
                    >
                      {adminBooking ? "Pay Now" : "Confirm and pay advance"}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
    <div>
      <Modal show={props?.isShow} animation={false} size="xl">
        {toasterDetails && <Toaster details={toasterDetails} />}
        <Modal.Body>
          {spinnerOverlay}
          <div className="row ">
            <div className="col-md-6 col-12">
              <h3 className="theater_name text-center fw-b">
                <span className="logo-color-1 ">
                  {theaterForBooking?.name?.toUpperCase()}
                </span>
                &nbsp;
                <span>Theatre</span>
              </h3>
              <hr className="m-0 border-danger bg-danger" size='6' style={{"opacity":1}}></hr>
              <div className="m-3 mt-0 p-3 border border-danger light-orange-color-bg" id="reviewDetails">
                <ul className="list-unstyled">
                  <li className="text-danger fw-bold fs-6">Total: <span><i className="bi bi-currency-rupee"></i> {props.totalCost}</span></li>
                  
          <li> <i className="bi bi-calendar"></i> <span> {props.selectedValues.eventDate}</span></li>
          <li> <i className="bi bi-person-circle"></i> 
          <span> {props.selectedValues.personsCount} Persons</span> - 
          <span className="text-danger"><i className="bi bi-currency-rupee"></i>{props.totalCost}</span></li>
                {Object.entries(props?.selectedAddons).map(([key, value]) => {
                  console.log(value);
                  return renderSelectedProps(value);
                })}
                </ul>
              </div>
            </div>
            <div className="col-md-6 col-12">
                <Card  className="border-3 border-orange">
                  <h3
                    className="fs-5 py-2 fw-bold bg-color-1-opa text-white text-center"
                  >
                    Your details
                  </h3>
                  <Card.Body>
                    <Card.Text className="col-md-12">
                      {contactSummaryModal}
                      <p
                        className="p-2 m-2 mt-3 light-orange-color-bg text-muted"
                        style={{ borderRadius: "10px" }}
                      >
                        We collect an advance amount of ₹ 650 + convenience fee
                        (₹50) to book the slot. Remaining bill will be collected
                        at the time of check-out.
                      </p>
                    </Card.Text>
                  </Card.Body>
                </Card>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button
            variant="btn btn-dark bg-transparent text-dark fw-bold  fs-6 text-center"
            onClick={() => props?.closeReview()}
          >
            Close
          </Button>
          <Button
            variant="btn text-white  fs-6 text-center"
            onClick={submitFn}
          >
            Proceed to Pay
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInstructions}
        backdrop="static"
        keyboard={false}
        size="lg"
        onHide={closeInstructionsModal}
      >
        <Modal.Body>
          <h3 className="text-center mb-3">Important Instructions</h3>
          <p className="px-3 px-md-5 bg-danger text-white rounded-4 text-sm mt-2">Refund Policy : Partial advance amount (Rs 500/-) will be refundable if you cancel the slot atleast 72 hours prior to your booking time.</p>
          <ul style={{ listStyleType: "decimal" }}>
            <li>
            Smoking and Consumption of Alcohol is strictly prohibited inside the Theatres.
            </li>
            <li>You need to bring your own OTT accounts to watch the content.</li>
            <li>Party poppers, foam and Champaigne is not allowed inside the theatres, considering the sensitivity of the Theatres.</li>
            <li>Outside food is strictly prohibited, considering the sensitivity of carpets and recliners inside the Theatres.</li>
            <li>We charge full for children above or equal to 5 years and half for those who are below 5 years</li>
            <li>Right to admission is reserved by the Management.</li>
            <li>If Professional photography choosen, timing of the photography is according to the availability of the photographer. Only soft copies will be provided.</li>
            {/* <li><b>We collect an advance amount of ₹ 700 to book the slot, Our team will reach out to you regarding this.</b></li> */}
            </ul>
            <div className="justify-content-center d-md-flex">
            <Form.Check 
            type="checkbox" id="confirmInstructions" label="I agree to all the above conditions." checked={change} onChange={handleAgreementChange}
          /></div>

        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <Button
            variant="btn btn-dark text-white  fs-6 text-center"
            onClick={closeInstructionsModal}
          >
            Close
          </Button>
          <div className="d-flex flex-wrap gap-2">
            {adminBooking ? (
              <Button
                variant="btn btn-outline-light text-white fs-6 text-center"
                onClick={() => savePayLater()}
                disabled={!change}
              >
                Save and Pay Later
              </Button>
            ) : null}
            <Button
              variant="btn btn-danger text-white  fs-6 text-center"
              onClick={() => savePayNow()}
              disabled={!change}
              id="rzp-button1"
            >
              {adminBooking ? "Pay Now" : "Confirm and Pay Advance"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      </div>
    </>
  );
}

export default ReviewAddons;
